import { CommonModule } from '@angular/common';
import { LocalDatePipe } from '@anihis/shared/src/pipes/local-date.pipe';
import {
  ChangeDetectorRef,
  Component,
  inject,
  signal,
  ElementRef,
  ViewChild,
  effect,
} from '@angular/core';
import { explicitEffect } from 'ngxtension/explicit-effect';
import { TabsStateService } from '../../shared/services/tabs-state.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { CardHistoryComponent } from './card-history/card-history.component';
import { TranslocoModule } from '@jsverse/transloco';
import { FormBaseComponent } from '@anihis/shared/src/shared/base-components/form-base.component';
import { OwnerCardStateService } from '../../shared/services/owner-card-state.service';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { EmptyFallbackHtmlPipe } from '../../shared/pipe/empty-fallback-html.pipe';
import { CardsStore } from './cards.store';
import { LoadingStatus } from '../../shared/enums/loading';
import { debounceTime, distinctUntilChanged, finalize } from 'rxjs';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { LoadingSpinnerComponent } from '../../shared/component/loading-spinner/loading-spinner.component';
import { LoadingSkeletonComponent } from '../../shared/component/loading-skeleton/loading-spinner.component';
import { TourOverlayComponent } from '../../shared/component/tour/tour.component';
import { LoadingOverlayComponent } from '../../shared/component/loading-overlay/loading-overlay.component';
import { AnimalCardMobileCardComponent } from '../../shared/component/animal-card-mobile-card/animal-card-mobile-card.component';

export interface CardFilter {
  search: string | null;
}

@Component({
  standalone: true,
  selector: 'anihis-cards',
  templateUrl: './cards.component.html',
  styleUrls: ['./cards.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatChipsModule,
    MatIconModule,
    MatTableModule,
    MatTooltipModule,
    MatSlideToggleModule,
    CardHistoryComponent,
    TranslocoModule,
    NgxSkeletonLoaderModule,
    EmptyFallbackHtmlPipe,
    InfiniteScrollDirective,
    LoadingSpinnerComponent,
    LoadingSkeletonComponent,
    LoadingOverlayComponent,
    TourOverlayComponent,
    AnimalCardMobileCardComponent,
    LocalDatePipe,
  ],
  providers: [CardsStore],
})
export class CardsComponent extends FormBaseComponent {
  private ownerCardStateService = inject(OwnerCardStateService);
  private tabsService = inject(TabsStateService);
  private cdr = inject(ChangeDetectorRef);
  store = inject(CardsStore);

  isGeneratingIntakePdf = signal(false);

  animalUid = this.ownerCardStateService.animalUid;
  ownerUid = this.ownerCardStateService.ownerUid;

  isShowCard = signal(false);

  @ViewChild('tableWrapper', { static: false })
  tableWrapperElement!: ElementRef;
  onScrollDown = signal(false);

  readonly status = LoadingStatus;
  previewData = undefined;
  displayedColumns: string[] = [
    'Wanted',
    'Card number',
    'Name',
    'Breed',
    'Gender',
    'Date Of Birth',
    'Microchip',
    'Owner name',
    'Address',
    'Email',
    'Mobile number',
    'Actions',
  ];

  cardData: any;

  override form = this.fb.group({
    search: this.fb.control<string>(''),
  });

  constructor() {
    super();

    const storedAnimalUid = localStorage.getItem('animalUid')?.trim();
    const storedOwnerUid = localStorage.getItem('ownerUid')?.trim();
    if (storedAnimalUid && storedOwnerUid) {
      this.ownerCardStateService.setUids(
        storedAnimalUid,
        storedOwnerUid,
        true,
      );
    }

    explicitEffect([this.tabsService.getCloseCount('Cards')], ([cnt]) => {
      if (typeof cnt === 'number' && cnt > 0) {
        this.isShowCard.set(false);
        this.cardData = undefined;
        localStorage.removeItem('animalUid');
        localStorage.removeItem('ownerUid');
      }
    });

    explicitEffect([this.ownerCardStateService.openCardIntent], ([intent]) => {
      if (intent <= 0) {
        return;
      }

      const resolvedAnimalUid =
        this.ownerCardStateService.animalUid()?.trim() ||
        localStorage.getItem('animalUid')?.trim() ||
        '';
      const resolvedOwnerUid =
        this.ownerCardStateService.ownerUid()?.trim() ||
        localStorage.getItem('ownerUid')?.trim() ||
        '';

      if (resolvedAnimalUid && resolvedOwnerUid) {
        this.openCardHistory(resolvedAnimalUid, resolvedOwnerUid);
      }
    });

    // Reset scroll when switching to this tab
    effect(() => {
      const index = this.tabsService.selectedIndex();
      const currentTab = this.tabsService.tabs()[index];
      if (currentTab === 'Cards' && this.tableWrapperElement) {
        this.tableWrapperElement.nativeElement.scrollTop = 0;
      }
    });

    if (
      storedAnimalUid &&
      storedOwnerUid &&
      this.ownerCardStateService.openCardIntent() > 0
    ) {
      this.openCardHistory(storedAnimalUid, storedOwnerUid);
    }

    this.form
      .get('search')
      ?.valueChanges.pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((value) => {
        this.store.updateSearch(value!);
      });
  }

  private openCardHistory(animalUid: string, ownerUid: string): void {
    if (
      this.isShowCard() &&
      this.cardData?.animalUid === animalUid &&
      this.cardData?.owner?.uid === ownerUid
    ) {
      return;
    }

    this.cardData = {
      animalUid,
      owner: { uid: ownerUid },
    };
    this.isShowCard.set(true);
    localStorage.setItem('animalUid', animalUid);
    localStorage.setItem('ownerUid', ownerUid);
    this.cdr.markForCheck();
  }

  openPreviewDialog(e: any, data: any) {
    e?.stopPropagation?.();
    this.openCardPreview(data);
  }

  openCardPreview(data: any) {
    this.openCardHistory(data.animalUid, data.owner.uid);
    this.cardData = data;
  }

  showCardHistory() {
    this.isShowCard.set(false);
    this.cardData = undefined;
    // refresh the cards list when returning from card history
    this.store.fetchCards().subscribe({
      error: (err) =>
        console.error('Failed to fetch cards on history close', err),
    });
  }

  clearSearch() {
    this.form.get('search')?.setValue('');
  }

  loadMore() {
    if (
      this.store.status() !== this.status.Loading &&
      this.store.pagination()?.hasNextPage
    ) {
      this.store.loadMoreData().subscribe();
    }
  }

  generateIntakeFormPdf(): void {
    if (this.isGeneratingIntakePdf()) return;

    this.isGeneratingIntakePdf.set(true);
    this.store
      .exportIntakeFormPdf()
      .pipe(finalize(() => this.isGeneratingIntakePdf.set(false)))
      .subscribe();
  }
}
